import { useEffect, useRef } from "react";

/**
 * Shared hook to track if we've had an active stream in this browser session.
 *
 * This is used by Firebase subscription hooks (useAgentStatus, useRealtimeMessages)
 * to determine if they should subscribe to Firestore:
 * - On page reload (no prior stream): Subscribe to detect active agent
 * - After stream ends (had prior stream): Don't subscribe to avoid UI flashing
 *
 * The distinction prevents the "agent busy" banner from appearing after a normal
 * stream ends due to Firestore update delay.
 *
 * @param hasActiveStream - Current active stream status from useStreamWithBackend
 * @returns Object with:
 *   - hadActiveStream: Whether we've ever had an active stream this session
 *   - shouldSubscribe: Whether Firebase subscription should be active
 */
export function useStreamActivityGuard(hasActiveStream: boolean) {
  // Track if we've ever had an active stream in this browser session
  const hadActiveStreamRef = useRef(false);

  // Mark that we've had an active stream (one-way flag)
  useEffect(() => {
    if (hasActiveStream) {
      hadActiveStreamRef.current = true;
    }
  }, [hasActiveStream]);

  return {
    /**
     * True if we've ever had an active stream in this browser session.
     * Once true, it never becomes false again (session-scoped).
     */
    hadActiveStream: hadActiveStreamRef.current,

    /**
     * True if Firebase subscriptions should be active.
     * This is true only on page reload (never had a stream) and no current stream.
     */
    shouldSubscribe: !hadActiveStreamRef.current && !hasActiveStream,
  };
}
